import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { cn } from "@/lib/utils";

function isTouchDevice() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

const HoverCard = ({
  children,
  openDelay = 200, // default for desktop
  closeDelay = 100,
  ...rest
}: React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root>) => {
  const [open, setOpen] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);

  React.useEffect(() => {
    setIsTouch(isTouchDevice());
  }, []);

  return (
    <HoverCardPrimitive.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={isTouch ? 0 : openDelay}
      closeDelay={closeDelay}
      {...rest}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        if (child.type?.displayName === "HoverCardTrigger") {
          return React.cloneElement(child, {
            onClick: isTouch ? () => setOpen((prev) => !prev) : undefined,
            onMouseEnter: !isTouch ? () => setOpen(true) : undefined,
            onMouseLeave: !isTouch ? () => setOpen(false) : undefined,
          });
        }

        return child;
      })}
    </HoverCardPrimitive.Root>
  );
};

const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(({ children, ...props }, ref) => {
  return (
    <HoverCardPrimitive.Trigger ref={ref} asChild {...props}>
      <div>{children}</div>
    </HoverCardPrimitive.Trigger>
  );
});

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      "z-50 w-64 rounded-md border bg-popover p-4 text-xs text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent };
